import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiService } from '../../../../services/api';
import { Empenho, EtapaEmpenho } from '../../../../types/empenhos';

interface FormRetrocederEtapaProps {
  empenho: Empenho;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FormRetrocederEtapa: React.FC<FormRetrocederEtapaProps> = ({
  empenho,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [observacoes, setObservacoes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Função para obter a etapa anterior (ATUALIZADO - Novo Fluxo)
  const getEtapaAnterior = (etapa: EtapaEmpenho): string => {
    if (etapa === 'NF_SAIDA' && empenho.atendido_por_estoque == 1) {
      return 'COMPRAS';
    }
    const fluxo: Record<EtapaEmpenho, string> = {
      PLANEJAMENTO: '-', // Mestre não retrocede
      COMPRAS: '-', // Não pode retroceder de COMPRAS
      PAGAMENTO: 'COMPRAS',
      PAGO: 'PAGAMENTO', // legado
      NF_ENTRADA: 'PAGAMENTO',
      NF_SAIDA: 'NF_ENTRADA',
      ENTREGA: 'NF_SAIDA',
      CONCLUIDO: 'ENTREGA',
      CANCELADO: '-'
    };
    return fluxo[etapa] || '-';
  };

  // Função para obter os efeitos do retrocesso (ATUALIZADO - Novo Fluxo + Legado)
  const getEfeitosRetrocesso = (etapa: EtapaEmpenho): string[] => {
    const efeitos: string[] = [];

    switch (etapa) {
      case 'COMPRAS':
        efeitos.push('⚠️ Não é possível retroceder desta etapa');
        efeitos.push('A Solicitação de Compra já foi enviada ao fornecedor');
        break;

      case 'PAGAMENTO':
        efeitos.push('As parcelas serão removidas do sistema');
        efeitos.push('O registro do pagamento será cancelado');
        efeitos.push('⚠️ Se já houver valores pagos, será necessário estorno manual');
        efeitos.push('Os dados bancários serão mantidos');
        efeitos.push('Voltar para: COMPRAS');
        break;

      case 'PAGO':
        efeitos.push('A Conta a Pagar será reaberta');
        efeitos.push('⚠️ Se já houver valores pagos, será necessário estorno manual');
        efeitos.push('Os dados da NF de compra serão limpos');
        efeitos.push('Voltar para: PAGAMENTO');
        break;

      case 'NF_SAIDA':
        if (empenho.atendido_por_estoque == 1) {
          efeitos.push('A flag de "atendido por estoque" será removida');
          efeitos.push('O empenho voltará para a etapa de Compras');
          efeitos.push('Nenhuma SC, parcela ou CP foi gerada — não há registros para cancelar');
          efeitos.push('O estoque NÃO será alterado (não houve entrada)');
          efeitos.push('Voltar para: COMPRAS');
        } else {
          efeitos.push('A Conta a Receber será cancelada');
          efeitos.push('⚠️ Se já houver recebimentos, será necessário estorno manual');
          efeitos.push('Os dados da NF de venda serão limpos');
          efeitos.push('A data de envio será removida');
          efeitos.push('Voltar para: NF_ENTRADA');
        }
        break;

      case 'ENTREGA':
        efeitos.push('O estoque será revertido (itens voltam ao estoque)');
        efeitos.push('Os dados de entrega serão limpos');
        efeitos.push('Voltar para: NF_SAIDA');
        break;

      default:
        efeitos.push('Não é possível retroceder desta etapa');
    }

    return efeitos;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!observacoes.trim()) {
      toast.error('Por favor, informe o motivo do retrocesso');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiService.retrocederEtapaEmpenho({
        id_empenho: empenho.id,
        observacoes: observacoes.trim()
      });

      if (response.success) {
        toast.success('Etapa retrocedida com sucesso!');
        setObservacoes('');
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao retroceder etapa');
      }
    } catch (error: any) {
      console.error('Erro ao retroceder etapa:', error);
      toast.error(error.message || 'Erro ao retroceder etapa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setObservacoes('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const efeitos = getEfeitosRetrocesso(empenho.etapa);
  const etapaAnterior = getEtapaAnterior(empenho.etapa);

  // Verificar se a etapa permite retrocesso
  const permiteRetrocesso = etapaAnterior !== null && etapaAnterior !== '-';

  if (!permiteRetrocesso) {
    return (
      <>
        <div className="modal fade show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger">
                <h5 className="modal-title">
                  <i className="ti ti-x me-2" />
                  Retrocesso Não Permitido
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-body text-center py-4">
                <i className="ti ti-x-circle text-danger fs-1 mb-3"></i>
                <h6>Não é possível retroceder da etapa atual</h6>
                <p className="text-muted">
                  A etapa "{empenho.etapa}" não permite retrocesso para etapas anteriores.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleClose}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" onClick={handleClose} />
      </>
    );
  }

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-warning">
              <h5 className="modal-title">
                <i className="ti ti-arrow-left me-2" />
                Retroceder Etapa
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                disabled={isSubmitting}
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Fluxo de retrocesso */}
                <div className="mb-4 text-center">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div>
                      <div className="badge badge-soft-info p-3">
                        {empenho.etapa}
                      </div>
                      <div className="text-muted small mt-1">Atual</div>
                    </div>
                    <i className="ti ti-arrow-left fs-3 text-warning" />
                    <div>
                      <div className="badge badge-soft-secondary p-3">
                        {etapaAnterior}
                      </div>
                      <div className="text-muted small mt-1">Voltará para</div>
                    </div>
                  </div>
                </div>

                {/* Efeitos do retrocesso */}
                <div className="mb-4">
                  <h6 className="mb-2">
                    <i className="ti ti-info-circle me-2" />
                    Efeitos do retrocesso:
                  </h6>
                  <ul className="list-group">
                    {efeitos.map((efeito, index) => (
                      <li
                        key={index}
                        className={`list-group-item ${
                          efeito.includes('⚠️') ? 'list-group-item-warning' : ''
                        }`}
                      >
                        {efeito}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Motivo do retrocesso */}
                <div className="mb-3">
                  <label htmlFor="observacoes" className="form-label">
                    Motivo do Retrocesso <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="observacoes"
                    className="form-control"
                    rows={3}
                    placeholder="Descreva o motivo do retrocesso..."
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                  <div className="form-text">
                    Este motivo ficará registrado no histórico.
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={isSubmitting || observacoes.trim().length < 5}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Retrocedendo...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-arrow-left me-2" />
                      Confirmar Retrocesso
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={handleClose} />
    </>
  );
};

export default FormRetrocederEtapa;
